import { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import {
  Row,
  Col,
  Spin,
  Button,
  notification,
  Tag,
  Typography,
  Form,
  Tabs,
} from 'antd';
import { PitstopModal } from 'shared';
import styled from 'styled-components';
import { AppStore } from 'stores';

import WorkOrderLeanForm from 'components/WorkOrder/WOLeanDetailsForm';
import { ShopStore } from 'stores';

import {
  getPriorityTagColor,
  getPriorityTagDescription,
} from 'stores/Classes/IssueObject';

const { Text } = Typography;

const StyledModal = styled(PitstopModal)`
  && {
    .ant-modal-content {
      padding: 0;
    }

    .ant-modal-header {
      background-color: #02103d;
      border-bottom: 1px solid #02103d;
      padding: 20px;
      .ant-modal-title {
        color: white;
      }
    }

    .ant-modal-body,
    .ant-modal-footer {
      padding: 10px 20px;
    }

    .ant-modal-footer {
      padding: 20px;
      border-top: solid 1px #efefef;

      display: flex;

      .footer-text {
        flex: 1;
        text-align: left;
        font-weight: 500;
      }
    }
    .ant-modal-close-x {
      color: #ffffffff !important;
      &:hover {
        color: #777777ff !important;
      }
    }
  }
`;

const Container = styled.div`
  padding-right: 2vw;
`;

const IssueLine = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 10px;
`;

const CREATE_WORK_ORDER_MODAL_ID = 'CREATE_WORK_ORDER_MODAL_ID';

const CreateWorkOrderModal = ({
  modalId,
  title,
  isLoading = false,
  onSave = () => {},
  loading = false,
  issues = [],
  type,
  issuesGroupedByVehicles,
  visible,
  tabToClose,
}) => {
  // console.log('🐳🐳🐳🐳🐳🐳', issuesGroupedByVehicles);
  const [vehicleIds, setVehicleIds] = useState(
    issuesGroupedByVehicles ? Object.keys(issuesGroupedByVehicles) : []
  );
  const [formRef, setFormRef] = useState(null);
  const [form] = Form.useForm();
  const [formValuesByTab, setFormValuesByTab] = useState({});
  const [activeTab, setActiveTab] = useState(vehicleIds[0]);

  const history = useHistory();

  useEffect(() => {
    if (visible) {
      const ids = Object.keys(issuesGroupedByVehicles);
      setVehicleIds(ids);
      setActiveTab(ids[0]);
    }
  }, [visible]);

  useEffect(() => {
    // console.log('Tab to close:', tabToClose);
    handleTabClose(tabToClose);
  }, [tabToClose]);

  const handleSave = async () => {
    try {
      if (type === 'defects') {
        if (formRef && typeof formRef.validateFields === 'function') {
          const values = await formRef.validateFields();
          onSave({
            ...values,
            woServiceTask: formRef.state?.currentServiceTask,
            woDescription: formRef.state?.currentDescription,
          });
        } else if (formRef && formRef.props && formRef.props.form) {
          formRef.props.form.validateFields((err, values) => {
            if (err) {
              throw err;
            }
            onSave({
              ...values,
              woServiceTask: formRef.state?.currentServiceTask,
              woDescription: formRef.state?.currentDescription,
            });
          });
        } else {
          const values = await form.validateFields();
          onSave({
            ...values,
          });
        }
      } else {
        const currentFormRef = formRef[activeTab];
        if (
          currentFormRef &&
          typeof currentFormRef.validateFields === 'function'
        ) {
          const values = await currentFormRef.validateFields();
          onSave(
            {
              ...values,
              woServiceTask: currentFormRef.state?.currentServiceTask,
              woDescription: currentFormRef.state?.currentDescription,
            },
            activeTab
          );
        } else {
          const values = await form.validateFields();
          onSave({ ...values }, activeTab);
        }
      }
    } catch (err) {
      let friendlyMsg = '';

      if (err && err.errorFields && err.errorFields.length > 0) {
        friendlyMsg = err.errorFields[0].errors[0] || 'Error validating form';
      } else if (err && typeof err === 'object') {
        const errorKeys = Object.keys(err);
        if (
          errorKeys &&
          errorKeys[0] &&
          err[errorKeys[0]] &&
          err[errorKeys[0]].errors &&
          err[errorKeys[0]].errors.length &&
          err[errorKeys[0]].errors[0].message
        ) {
          friendlyMsg = err[errorKeys[0]].errors[0].message;
        } else {
          friendlyMsg = 'Error on saving Work Order';
        }
      } else {
        friendlyMsg = 'Error on saving Work Order';
      }

      notification.error({
        message: 'Oops!',
        key: 'orderSaveError',
        description: <Container>{friendlyMsg}</Container>,
      });
    }
  };

  const handleTabClose = (target) => {
    // console.log('Closing tab:', target);
    if (!target) return;
    if (vehicleIds.length > 1) {
      vehicleIds.findIndex((id) => id === target) === vehicleIds.length - 1
        ? setActiveTab(
            vehicleIds[vehicleIds.findIndex((id) => id === target) - 1]
          )
        : setActiveTab(
            vehicleIds[vehicleIds.findIndex((id) => id === target) + 1]
          );
    } else {
      handleModalClose();
    }
    setVehicleIds(vehicleIds.filter((id) => id !== target));
  };

  const handleModalClose = () => {
    setActiveTab(null);
    visible = false;
    AppStore.openModals.set(modalId, false);
    notification.info({
      message: 'View and Edit Work Orders',
      description: (
        <div>
          You can view details and edit your work orders in the{' '}
          <Button
            style={{ padding: 0 }}
            type="link"
            onClick={() => history.push('/work-order')}
          >
            Work Order Management
          </Button>{' '}
          tab.
        </div>
      ),
    });
  };

  if (type === 'defects') {
    return (
      <StyledModal
        id={modalId}
        title={title}
        width={850}
        okText="Confirm"
        footer={
          <>
            <Text className="footer-text">
              You can add more details, such as description, cost, etc., to this
              work order once it's created
            </Text>
            <Button
              key="back"
              onClick={() => {
                AppStore.openModals.set(CREATE_WORK_ORDER_MODAL_ID, false);
              }}
            >
              Cancel
            </Button>
            <Button
              key="submit"
              type="primary"
              loading={loading}
              onClick={handleSave}
            >
              Create
            </Button>
          </>
        }
      >
        {isLoading ? (
          <Spin size="large" />
        ) : (
          <Form form={form}>
            <Row>
              <Col span={24}>
                <WorkOrderLeanForm
                  shopId={ShopStore.currentShop.id}
                  childRef={setFormRef}
                  workOrder={{}}
                  type="defects"
                  form={form}
                />
              </Col>
            </Row>
            {/* Show issues list if issues is not empty */}
            {issues.length > 0 && (
              <Row style={{ marginTop: 15 }}>
                <Col span={24}>
                  <Text strong style={{ marginBottom: 10, display: 'block' }}>
                    Defects that will be added to this Work Order
                  </Text>
                  {issues.map((issue) => (
                    <IssueLine key={issue.id}>
                      <Text>
                        {issue.defectType}: {issue.comment}
                      </Text>
                    </IssueLine>
                  ))}
                </Col>
              </Row>
            )}
          </Form>
        )}
      </StyledModal>
    );
  } else {
    return (
      <StyledModal
        id={modalId}
        title={title}
        width={850}
        okText="Confirm"
        visible={true}
        closable={true}
        footer={
          <>
            <Button
              key="back"
              // onClick={handleModalClose}
              onClick={() => handleTabClose(activeTab)}
            >
              Close Tab
            </Button>
            <Button
              key="submit"
              type="primary"
              loading={loading}
              onClick={handleSave}
            >
              Save Work Order
            </Button>
          </>
        }
      >
        {isLoading ? (
          <Spin size="large" />
        ) : (
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            // type={'editable-card'} hideAdd onEdit={handleTabClose}
          >
            {vehicleIds.map((vehicleId) => {
              const vehicleIssues = issuesGroupedByVehicles[vehicleId] || [];
              const vehicleName = vehicleIssues[0]?.car?.name || '';
              return (
                <Tabs.TabPane
                  tab={`Work order for Unit ${vehicleName}`}
                  key={vehicleId}
                >
                  <Form form={form}>
                    <Row>
                      <Col span={24}>
                        <WorkOrderLeanForm
                          shopId={
                            ShopStore.currentShop.parentShopId ||
                            ShopStore.currentShop.id
                          }
                          childRef={(ref) =>
                            setFormRef((prev) => ({
                              ...prev,
                              [vehicleId]: ref,
                            }))
                          }
                          workOrder={{}}
                          type="issues"
                          form={form}
                        />
                      </Col>
                    </Row>
                    {/* Show issues list if issues is not empty */}
                    {vehicleIssues.length > 0 && (
                      <Row style={{ marginTop: 15 }}>
                        <Col span={24}>
                          <Text
                            strong
                            style={{ marginBottom: 10, display: 'block' }}
                          >
                            Issues that will be added to this Work Order
                          </Text>
                          {vehicleIssues.map((issue) => (
                            <IssueLine key={issue.id}>
                              <Tag color={getPriorityTagColor(issue.priority)}>
                                {getPriorityTagDescription(issue.priority)}
                              </Tag>
                              <Text>{issue.name}</Text>
                            </IssueLine>
                          ))}
                        </Col>
                      </Row>
                    )}
                  </Form>
                </Tabs.TabPane>
              );
            })}
          </Tabs>
        )}
      </StyledModal>
    );
  }
};

CreateWorkOrderModal.propTypes = {
  modalId: PropTypes.string.isRequired,
  title: PropTypes.string,
  isLoading: PropTypes.bool,
  onSave: PropTypes.func,
  issues: PropTypes.array,
  loading: PropTypes.bool,
  type: PropTypes.string,
};

export default CreateWorkOrderModal;
